import React from 'react';
import { makeStyles, type Theme } from '@material-ui/core';
import { colors } from '@skyslope/mache';
import { Document } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Rnd } from 'react-rnd';
import { type LaunchDarklyFlags, withLaunchDarkly } from '@/common/launchDarkly';
import { useIsSkySlopeMobileApp } from '../../common/utils';
import { BLOCK_TYPES, BLOCK_TYPE_KEYS, readOnlyKey, SHELF_WIDTH } from '../../lib/constants';
import { useIsMediumScreenMediaQuery, useIsSmallScreenOrMobileApp } from '../../lib/isSmallScreen';
import pdfCache from '../../lib/pdfCache';
import { useShallowSelector, useDispatch } from '../../lib/reduxHooks';
import { blockMenuResize } from '../../lib/utils';
import * as actions from '../../store/senderBuild/actions';
import { type IBlock, type IDocument, type StrikeShape } from '../../store/senderBuild/types';
// eslint-disable-next-line import/no-named-as-default
import AutoBlocks from './AutoBlocks';
import { BlockActions } from './BlockShelf/BlockActions';
import { BlockProperties } from './BlockShelf/BlockProperties';
import SignerBlockToolbar from './BlockToolbars/SignerBlockToolbar';
import StrikeBlockToolbar from './BlockToolbars/StrikeBlockToolbar';
import PdfPage from './PdfPage';
import Subheader from './Subheader';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    bottom: 64,
    position: 'fixed',
    overflow: 'auto',
    left: SHELF_WIDTH + 1,
    right: 0,
    top: '156px',
    borderBottom: `1px solid ${colors.grey[400]}`,
  },
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    left: SHELF_WIDTH + 1,
    borderBottom: `1px solid ${colors.grey[400]}`,
    marginBottom: '100px',
  },
  mobileAppWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    left: SHELF_WIDTH + 1,
    marginTop: '20px',
    marginBottom: theme.spacing(12),
  },
  document: {
    maxWidth: '100%',
  },
  mobileDocument: {
    marginTop: 0,
  },
  documentName: {
    fontSize: '16px',
    color: colors.grey[800],
    fontWeight: 600,
    margin: `${theme.spacing(3)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
  },
  '@global': {
    '.MuiSnackbar-root': {
      marginBottom: 64,
    },
  },
}));

type Position = 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

function PdfViewer(props: { flags: LaunchDarklyFlags }) {
  const { flags } = props;

  const dispatch = useDispatch();
  const store = useShallowSelector((state) => ({
    documents: state.senderBuild.documents,
    selectedBlocks: state.senderBuild.selectedBlocks,
    blocks: state.senderBuild.blocks,
    pageDimensions: state.senderBuild.pageDimensions,
    zoom: state.senderBuild.zoom,
    stateBlockMenuPos: state.senderBuild.blockMenuPos,
    copyCache: state.senderBuild.copyCache,
    checkboxGroupd: state.senderBuild.groups,
    signers: state.senderBuild.signers,
    activeSigner: state.senderBuild.activeSigner,
    mouseCoords: state.senderBuild.mouseCoords ?? { x: 0, y: 0 },
  }));

  const [currentDocument, setCurrentDocument] = React.useState(
    store.documents.length ? store.documents[0].documentId : null
  );
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [visibleFullPage, setVisibleFullPage] = React.useState(0);
  const [docsLoaded, setDocsLoaded] = React.useState({});
  const [vizDisabled, setVizDisabled] = React.useState(false);

  const isFullScreen = useIsMediumScreenMediaQuery();
  const isMobileApp = useIsSkySlopeMobileApp();
  const filteredSigners = store.signers.filter((signer) => signer.signingGroup !== '');
  const oneSelectedBlock = store.selectedBlocks.blockIndices.length === 1;
  const currentSelectedBlock =
    oneSelectedBlock &&
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    store.blocks[store.selectedBlocks?.documentId][store.selectedBlocks?.pageIndex][
      store.selectedBlocks?.blockIndices[0]
    ];
  const currentBlockType = BLOCK_TYPES.find((bt) => bt.key === currentSelectedBlock.blockType);
  const isStrikeBlock = currentBlockType?.key === BLOCK_TYPE_KEYS.STRIKE;
  const isRectangleBlock = currentBlockType?.key === BLOCK_TYPE_KEYS.RECTANGLE;
  const [pdfViewerCoords, setPDFViewerCoords] = React.useState({});
  const [pdfHeaderCoords, setPDFHeaderCoords] = React.useState({});
  const [blockMenuPos, setBlockMenuPos] = React.useState<{
    x: null | number;
    y: null | number;
    position: Position;
  }>({
    x: null,
    y: null,
    position: 'top',
  });

  const isMobile = useIsSmallScreenOrMobileApp();

  const classes = useStyles();
  const { documents, blocks } = store;
  let fullPageIndex = 0;

  React.useEffect(() => {
    const viewerCoords = document.querySelector('.PdfViewer')!.getBoundingClientRect();
    const headerCoords = document.querySelector('.pdfViewerHeader')!.getBoundingClientRect();
    setPDFViewerCoords(viewerCoords);
    setPDFHeaderCoords(headerCoords);
  }, []);

  const [isPasteButtonDisabled, setIsPasteButtonDisabled] = React.useState(true);
  React.useEffect(() => {
    if (store.copyCache.blocks.length > 0) {
      setIsPasteButtonDisabled(false);
      return;
    }
    setIsPasteButtonDisabled(true);
  }, [store.copyCache.blocks]);

  React.useEffect(() => {
    const shouldSetToolbarPosition =
      oneSelectedBlock && !currentSelectedBlock.groupId && pdfHeaderCoords && pdfViewerCoords;
    if (!shouldSetToolbarPosition) return;

    const blockCoords = document.querySelector(`#rnd-${currentSelectedBlock.blockId}`)?.getBoundingClientRect();
    if (!blockCoords) return;

    // since we are using the coordinates of the .PdfViewer now, we need to get the location of the
    // block on the document relative to .PdfViewer so these are the new coords for the selected block
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const translateX = blockCoords.left - pdfViewerCoords!.left;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const translateY = blockCoords.top - pdfViewerCoords!.top;
    let newX = translateX;
    let newY = translateY;
    let { position } = blockMenuPos;

    if (shouldSetToolbarPosition && !isMobile && !isStrikeBlock) {
      const signerToolbarCoords = getSignerBlockToolBarPos(
        translateX,
        translateY,
        blockCoords.width,
        blockCoords.height
      );
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newX = signerToolbarCoords.x;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newY = signerToolbarCoords.y;
    } else if (shouldSetToolbarPosition && isStrikeBlock) {
      const rightX = currentSelectedBlock.x * store.zoom + currentSelectedBlock.width * store.zoom;
      const leftX = currentSelectedBlock.x * store.zoom;
      const bottomLeftY = currentSelectedBlock.y * store.zoom;
      const bottomRightY = currentSelectedBlock.y * store.zoom + currentSelectedBlock.height * store.zoom;
      if (store.mouseCoords.x === leftX && store.mouseCoords.y === bottomLeftY) {
        position = 'topLeft';
      } else if (store.mouseCoords.x === rightX && store.mouseCoords.y === bottomLeftY) {
        position = 'topRight';
      } else if (store.mouseCoords.x === rightX && store.mouseCoords.y === bottomRightY) {
        position = 'bottomRight';
      } else if (store.mouseCoords.x === leftX && store.mouseCoords.y === bottomRightY) {
        position = 'bottomLeft';
      }

      const centerOfMenu = 234 / 2;
      const horizontalStrike = blockCoords.height < 5;
      const menuHeight = document.getElementById(`${currentSelectedBlock.blockId}-toolbar`)!.offsetHeight;
      if (store.mouseCoords.y !== null && store.mouseCoords.y === currentSelectedBlock.y * store.zoom) {
        // placing a strike
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        if (horizontalStrike && newY - 66 < pdfHeaderCoords!.height) {
          newY += 10;
        } else {
          newY -= 66;
        }
        if (horizontalStrike || position === 'topRight' || position === 'bottomRight') {
          newX = newX + blockCoords.width - centerOfMenu;
        } else {
          newX -= centerOfMenu;
        }
      } else if (store.mouseCoords.x !== null && store.mouseCoords.y !== null) {
        // drawing a strike
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        if (horizontalStrike && newY - 66 > pdfHeaderCoords!.height) {
          newY -= 66;
        } else {
          newY = newY + blockCoords.height + 10;
        }
        if (horizontalStrike || position === 'topRight' || position === 'bottomRight') {
          newX = newX + blockCoords.width - centerOfMenu;
        } else {
          newX -= centerOfMenu;
        }
      } else {
        const viewportWidth = window.innerWidth;
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
        const mobileX = scrollLeft + viewportWidth / 2 - 284 / 2;

        const viewportHeight = window.innerHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const mobileY = scrollTop + viewportHeight / 2.3;

        // @ts-expect-error -- This code was written prior to enforcing lint rules
        if (newY - menuHeight - 10 < pdfHeaderCoords!.height) {
          newY = isMobile ? mobileY : newY + blockCoords.height + 10;
        } else {
          newY = isMobile ? mobileY : newY - menuHeight - 10;
        }

        if (horizontalStrike) {
          newX = isMobile ? mobileX : newX + blockCoords.width - centerOfMenu;
        } else {
          newX = isMobile ? mobileX : newX - centerOfMenu;
        }
      }
    }

    setBlockMenuPos({ x: newX, y: newY, position });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.selectedBlocks.blockIndices]);

  React.useEffect(() => {
    const page = document.getElementById('scroll');
    const pageNum = page?.parentElement?.dataset?.fullPage;
    const viewer = document.querySelector('.PdfViewer');
    if (!viewer) return;
    if (pageNum === '0') {
      viewer.scrollTop = 0;
    } else {
      page?.scrollIntoView();
      viewer.scrollTop = 200;
    }
    setVizDisabled(false);
  }, [store.zoom]);

  React.useEffect(() => {
    if (isMobileApp) {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  function getSignerBlockToolBarPos(blockX: number, blockY: number, blockWidth: number, blockHeight: number) {
    const menuWidth = document.getElementById(`${currentSelectedBlock.blockId}-toolbar`)?.offsetWidth;
    const menuHeight = document.getElementById(`${currentSelectedBlock.blockId}-toolbar`)?.offsetHeight;
    if (menuHeight && menuWidth && pdfHeaderCoords && pdfViewerCoords) {
      const isCheckBox = currentBlockType?.key === BLOCK_TYPE_KEYS.CHECKBOX;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const draftBlockMenuPos: { x: number; y: number } = {};

      // @ts-expect-error -- This code was written prior to enforcing lint rules
      if (blockX + menuWidth > pdfViewerCoords!.width) {
        draftBlockMenuPos.x = blockX - menuWidth + blockWidth + (isCheckBox ? 5 : 0);
      } else {
        // isCheckBox is a temp fix to account for the add button on checkboxes. This can be changed when that is resolved with UX
        draftBlockMenuPos.x = blockX - (isCheckBox ? 5 : 0);
      }
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      if (blockY - menuHeight - 10 < pdfHeaderCoords!.height) {
        // isCheckBox is a temp fix to account for the add button on checkboxes. This can be changed when that is resolved with UX
        draftBlockMenuPos.y = blockY + blockHeight + (isCheckBox ? 36 : 10);
      } else {
        draftBlockMenuPos.y = blockY - menuHeight - 10;
      }

      return draftBlockMenuPos;
    }
    return {};
  }

  function zoom(zoomIn: true) {
    setVizDisabled(true);
    dispatch(zoomIn ? actions.zoomIn() : actions.zoomOut());
  }

  function setVisibility(documentId: string, pageIndex: number, fullPage: number) {
    setCurrentDocument(documentId);
    setCurrentPageIndex(pageIndex);
    setVisibleFullPage(fullPage);
    dispatch(actions.setCurrentPage(pageIndex));
    dispatch(actions.setCurrentDocument(documentId));
  }

  const intersactionRatioTracker = React.useRef<Record<string, number>>({});
  const [observer, setObserver] = React.useState<IntersectionObserver>();

  React.useEffect(() => {
    const intersectionObservor = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (vizDisabled) return;
        const tracker = intersactionRatioTracker.current;
        entries.forEach((e) => {
          tracker[e.target.id] = e.intersectionRatio;
        });

        const pages = Array.from(document.getElementsByClassName('pdf-viewer-page')) as HTMLDivElement[];
        const mostVisiblePage = pages.reduce<HTMLDivElement | null>((prev, current) => {
          return tracker[current.id] > (prev ? tracker[prev.id] : 0) ? current : prev;
        }, null);

        if (mostVisiblePage) {
          const { documentId, pageIndex, fullPage } = mostVisiblePage.dataset;
          if (
            currentDocument !== documentId ||
            Number(pageIndex) !== currentPageIndex ||
            Number(fullPage) !== fullPageIndex
          ) {
            setVisibility(documentId!, Number(pageIndex), Number(fullPage));
          }
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      }
    );

    setObserver(intersectionObservor);

    return () => {
      observer?.disconnect();
      intersactionRatioTracker.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [docsLoaded]);

  function dragMenu(_e: any, pos: any) {
    dispatch(actions.setBlockMenuPos(pos.x, pos.y));
    setBlockMenuPos({
      x: pos.x,
      y: pos.y,
      position: blockMenuPos.position,
    });
  }

  function changeProperty(key: string, value: any) {
    let pageBlocks: IBlock[] = [];
    if (
      blocks &&
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      store.selectedBlocks.documentId in blocks &&
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      store.selectedBlocks.pageIndex in blocks[store.selectedBlocks.documentId]
    ) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      pageBlocks = blocks[store.selectedBlocks.documentId][store.selectedBlocks.pageIndex];
    }
    store.selectedBlocks.blockIndices.forEach((index, i) => {
      const block = pageBlocks[index];
      const update = {
        [key]: value,
      };
      if (key === readOnlyKey) {
        update.assignedTo = value ? null : block.previouslyAssignedTo || store.activeSigner;
        if (value) {
          update.required = false;
          update.previouslyAssignedTo = block.assignedTo;
        }
      }
      dispatch(
        actions.editBlock(
          store.selectedBlocks.documentId!,
          store.selectedBlocks.pageIndex!,
          store.selectedBlocks.blockIndices[i],
          update
        )
      );
    });
  }

  function selectBlockToolbarSigner(e: any) {
    store.selectedBlocks.blockIndices?.forEach((blockIndex) => {
      const index = store.selectedBlocks.blockIndices.findIndex((i) => i == blockIndex);
      const block: IBlock =
        store.blocks[store.selectedBlocks.documentId!][store.selectedBlocks.pageIndex!][
          store.selectedBlocks.blockIndices[index]
        ];
      if (block.readOnly || block.groupId) return;
      dispatch(
        actions.editBlock(store.selectedBlocks.documentId!, store.selectedBlocks.pageIndex!, blockIndex, {
          assignedTo: ![BLOCK_TYPE_KEYS.STRIKE, BLOCK_TYPE_KEYS.RECTANGLE].includes(
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            store.selectedBlocks.blockTypes![blockIndex]
          )
            ? e.target.value
            : null,
        })
      );
    });
  }

  function handleColorClick(color: string) {
    dispatch(actions.changeActiveStrikeColor(color));
    dispatch(
      actions.editBlock(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        store.selectedBlocks.documentId,
        store.selectedBlocks.pageIndex,
        store.selectedBlocks.blockIndices[0],
        { color }
      )
    );
  }

  function handleShapeClick(strikeShape: StrikeShape) {
    dispatch(actions.changeActiveStrikeShape(strikeShape));
    dispatch(
      actions.editBlock(
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        store.selectedBlocks.documentId,
        store.selectedBlocks.pageIndex,
        store.selectedBlocks.blockIndices[0],
        { strikeShape }
      )
    );
  }

  function blockToolbar() {
    if (!currentSelectedBlock || currentSelectedBlock.groupId || (isMobile && !isStrikeBlock)) {
      return null;
    }
    function blockOptions() {
      return (
        <div className="flex flex-col w-full">
          <BlockActions
            blockType={currentBlockType?.key}
            isBlockToolbar
            isPasteButtonDisabled={isPasteButtonDisabled}
          />
          {!!currentBlockType?.properties?.length && (
            <BlockProperties
              isBlockToolbar
              firstBlock={currentSelectedBlock}
              // @ts-expect-error -- This code was written prior to enforcing lint rules
              blockTypeProperties={currentBlockType?.properties}
              changeProperty={changeProperty}
            />
          )}
        </div>
      );
    }

    return (
      <Rnd
        id={`${currentSelectedBlock.blockId}-toolbar`}
        position={{
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          x: store.stateBlockMenuPos.x || blockMenuPos.x,
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          y: store.stateBlockMenuPos.y || blockMenuPos.y,
        }}
        bounds=".PdfViewer"
        onDragStop={dragMenu}
        style={{ zIndex: 9999, position: 'fixed' }}
        enableResizing={blockMenuResize}
      >
        {isStrikeBlock ? (
          <StrikeBlockToolbar
            blockOptions={blockOptions()}
            block={currentSelectedBlock}
            handleColorClick={handleColorClick}
            handleShapeClick={handleShapeClick}
          />
        ) : (
          <SignerBlockToolbar
            blockOptions={blockOptions()}
            blocks={[currentSelectedBlock]}
            isDisabled={!!currentSelectedBlock.groupId || currentSelectedBlock.readOnly}
            selectSigner={selectBlockToolbarSigner}
            signers={filteredSigners}
          />
        )}
      </Rnd>
    );
  }

  const wrapperClass = isMobileApp ? classes.mobileAppWrapper : isFullScreen ? classes.mobileWrapper : classes.wrapper;
  const documentClass = isFullScreen ? `${classes.document} ${classes.mobileDocument} first:mt-8` : classes.document;
  return (
    <div className={`${wrapperClass} PdfViewer`}>
      {!isRectangleBlock && blockToolbar()}
      {isFullScreen ? null : (
        <Subheader zoom={zoom} currentDocumentId={currentDocument} currentPageIndex={currentPageIndex} />
      )}
      <AutoBlocks />
      {documents.map((document: IDocument) => {
        const res = (
          <div key={document.documentId} className={`${documentClass} `}>
            {document.isDownloaded && (
              <Document
                file={pdfCache[document.documentId]}
                loading=""
                onLoadSuccess={() => setDocsLoaded((prev) => ({ ...prev, [document.documentId]: true }))}
                onLoadError={(error) => console.error('Failed to load PDF document', document.documentId, error)}
                onSourceError={(error) => console.error('Failed to fetch PDF source', document.documentId, error)}
              >
                {Array(document.numberOfPages)
                  .fill(null)
                  .map((_, pageIndex: number) => {
                    const selectingOnThisPage =
                      store.selectedBlocks.documentId === document.documentId &&
                      store.selectedBlocks.pageIndex === pageIndex;
                    let pageBlocks: IBlock[] = [];
                    if (blocks && document.documentId in blocks && pageIndex in blocks[document.documentId]) {
                      pageBlocks = blocks[document.documentId][pageIndex];
                    }
                    return (
                      <PdfPage
                        observer={observer!}
                        key={fullPageIndex + pageIndex}
                        documentId={document.documentId}
                        pageIndex={pageIndex}
                        fullPage={fullPageIndex + pageIndex}
                        totalPages={document.numberOfPages!}
                        pageBlocks={pageBlocks}
                        selectedBlocks={selectingOnThisPage ? store.selectedBlocks.blockIndices : []}
                        flags={flags}
                        visiblePage={visibleFullPage}
                        isDownloaded
                        vizDisabled={vizDisabled}
                      />
                    );
                  })}
              </Document>
            )}
            {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
            {!docsLoaded[document.documentId] &&
              Array(document.numberOfPages)
                .fill(null)
                .map((_, pageIndex: number) => (
                  <PdfPage
                    observer={observer!}
                    key={`${fullPageIndex + pageIndex}-skeleton`}
                    documentId={document.documentId}
                    pageIndex={pageIndex}
                    fullPage={fullPageIndex + pageIndex}
                    totalPages={document.numberOfPages!}
                    pageBlocks={[]}
                    selectedBlocks={[]}
                    flags={flags}
                    visiblePage={visibleFullPage}
                  />
                ))}
          </div>
        );
        fullPageIndex += document.numberOfPages!;
        return res;
      })}
    </div>
  );
}

const withLd = withLaunchDarkly(PdfViewer);
export { withLd as PdfViewer };
